import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Col, Form, Input, Row, Select, Space, Typography, Modal, Button, message, Popconfirm, Divider } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation } from 'react-apollo';
import UPDATE_CAMPAIGN_ORDER_REQUESTOR from '../../../core/GraphQl/Mutations/UPDATE_CAMPAIGN_ORDER_REQUESTOR';
import { useCampaignRequestorContext } from '../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { HasAccess } from '@permify/react-role';

const { Text } = Typography;

const CampaignRequestorDetails = ({ formValues, helpers }) => {
  const {
    childrenOrg,
    loadingChildrenOrgs,
    currentUserOrg,
    selectedAdvertiser,
    fetchChildrenOrgs,
    currentUser,
    currentCampaignOrder,
  } = useCampaignRequestorContext();

  const [advertisers, setAdvertisers] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newIONumber, setNewIONumber] = useState('');
  const [ioSearchText, setIOSearchText] = useState('');
  const [updateCampaignOrder] = useMutation(UPDATE_CAMPAIGN_ORDER_REQUESTOR);

  // Debounce ref for advertiser search
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    if (childrenOrg && childrenOrg.length) {
      const options = childrenOrg.map((org) => ({
        value: org.id,
        label: org.name,
      }));
      // For cross-org campaigns, the selected advertiser may not be in
      // childrenOrg (e.g. parent org advertiser). Add it so the dropdown
      // displays the name instead of the raw ID.
      if (
        selectedAdvertiser &&
        !childrenOrg.some((org) => org.id === selectedAdvertiser.id)
      ) {
        options.unshift({
          value: selectedAdvertiser.id,
          label: selectedAdvertiser.name,
        });
      }
      setAdvertisers(options);
    } else {
      if (!selectedAdvertiser) return;

      setAdvertisers([
        {
          value: selectedAdvertiser.id,
          label: selectedAdvertiser.name,
        },
      ]);
    }
  }, [childrenOrg, selectedAdvertiser]);

  useEffect(() => {
    if (currentUser && currentUser.permission) {
      const authorized = currentUser.permission.isProcessor;
      setCanEdit(authorized);
    }
  }, [currentUser]);

  useEffect(() => {
    handleFetchAdvertisers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const handleFetchAdvertisers = useCallback((search = '') => {
    fetchChildrenOrgs({
      variables: {
        where: {
          OR: [{ parentOrg_some: { id: currentUserOrg.id } }, { id: currentUserOrg.id }],
          name_contains: search,
        },
        orderBy: 'name_ASC',
        first: 1500, // TODO: Apply virtual scrolling and pagination
      },
    });
  }, [fetchChildrenOrgs, currentUserOrg]);

  // Debounced search handler for advertiser search
  const handleDebouncedAdvertiserSearch = useCallback((searchText) => {
    // Clear existing timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Set new timeout (500ms delay)
    searchDebounceRef.current = setTimeout(() => {
      handleFetchAdvertisers(searchText);
    }, 500);
  }, [handleFetchAdvertisers]);

  const handleNameChange = (e) => {
    // Remove special characters in real-time using the helper
    const cleanValue = e.target.value.replace(/[^a-zA-Z0-9\s\-_]/g, '');
    if (cleanValue !== e.target.value) {
      helpers.updateField('name', cleanValue);
    }
  };

  const handleAddNewIONumber = async () => {

    if (!newIONumber.trim()) {
      return;
    }

    // Check for duplicate IO numbers
    const currentAdditional = formValues.additionalIONumbers || currentCampaignOrder?.additionalIONumbers || [];

    const isDuplicate = currentAdditional.some(
      io => io.value.toLowerCase() === newIONumber.toLowerCase()
    );

    if (isDuplicate) {
      message.warning('This IO Number already exists');
      return;
    }

    try {
      // If campaign order exists, persist to database immediately
      if (currentCampaignOrder?.id) {
        const { data } = await updateCampaignOrder({
          variables: {
            where: { id: currentCampaignOrder.id },
            data: {
              additionalIONumbers: {
                create: { value: newIONumber },
              },
            },
          },
        });

        const updatedCampaignOrder = data?.updateCampaignOrderRequestor;
        if (updatedCampaignOrder?.additionalIONumbers) {
          helpers.updateField('additionalIONumbers', updatedCampaignOrder.additionalIONumbers);
        }
        helpers.updateField('IONumber', newIONumber);
      } else {
        // For new campaigns, store in temporary form state (will be saved when campaign is created)
        const newEntry = {
          value: newIONumber,
          createdAt: new Date().toISOString(),
          __isStaged: true,
        };
        const updatedAdditional = [...currentAdditional, newEntry];

        helpers.updateField('additionalIONumbers', updatedAdditional);
        helpers.updateField('IONumber', newIONumber);
      }

      setIsModalVisible(false);
      setNewIONumber('');
    } catch (error) {
      console.error('Error adding IO number:', error);
    }
  };

  const handleDeleteIONumber = async (ioNumberId, ioNumberValue) => {
    const integrationIdsToDelete = [];
    const attachmentIdsToDelete = [];

    if (currentCampaignOrder?.externalCRMIntegrations) {
      currentCampaignOrder.externalCRMIntegrations.forEach((integration) => {
        const metadata = integration.metadata;
        const proposalNumber = metadata?.Proposal_Number__c;

        if (proposalNumber !== ioNumberValue) return;

        integrationIdsToDelete.push({ id: integration.id });

        const pandaDocRecords = metadata?.pandadoc__Panda_Doc_Documents__r?.records;
        if (!pandaDocRecords) return;

        pandaDocRecords.forEach((record) => {
          const uuid = record.pandadoc__UUID__c;
          if (!uuid) return;

          currentCampaignOrder.attachments?.forEach((attachment) => {
            if (attachment.referenceId === uuid) {
              attachmentIdsToDelete.push({ id: attachment.id });
            }
          });
        });
      });
    }

    if (!ioNumberId) {
      const currentAdditional = formValues.additionalIONumbers || [];
      const updatedAdditional = currentAdditional.filter((item) => item.value !== ioNumberValue);
      helpers.updateField('additionalIONumbers', updatedAdditional);

      if (formValues.IONumber === ioNumberValue) {
        helpers.updateField('IONumber', null);
      }
      return;
    }

    try {
      await updateCampaignOrder({
        variables: {
          where: { id: currentCampaignOrder.id },
          data: {
            additionalIONumbers: {
              delete: [{ id: ioNumberId }],
            },
            externalCRMIntegrations: {
              delete: integrationIdsToDelete,
            },
            attachments: {
              delete: attachmentIdsToDelete,
            },
          },
        },
      });

      const currentAdditional = formValues.additionalIONumbers || [];
      const updatedAdditional = currentAdditional.filter((item) => item.value !== ioNumberValue);
      helpers.updateField('additionalIONumbers', updatedAdditional);

      if (formValues.IONumber === ioNumberValue) {
        helpers.updateField('IONumber', null);
      }

      message.success('IO Number deleted successfully');
    } catch (error) {
      console.error(error);
      message.error('Failed to delete IO Number');
    }
  };

  const handleIONumberChange = (value) => {
    helpers.updateField('IONumber', value);
  };

  const handleAddIOFromSearch = async (value) => {
    if (!value.trim()) return;

    const currentAdditional = formValues.additionalIONumbers || currentCampaignOrder?.additionalIONumbers || [];
    const isDuplicate = currentAdditional.some(
      io => io.value.toLowerCase() === value.toLowerCase()
    );
    if (isDuplicate) {
      message.warning('This IO Number already exists');
      return;
    }

    try {
      if (currentCampaignOrder?.id) {
        const { data } = await updateCampaignOrder({
          variables: {
            where: { id: currentCampaignOrder.id },
            data: {
              additionalIONumbers: {
                create: { value },
              },
            },
          },
        });

        const updatedCampaignOrder = data?.updateCampaignOrderRequestor;
        if (updatedCampaignOrder?.additionalIONumbers) {
          helpers.updateField('additionalIONumbers', updatedCampaignOrder.additionalIONumbers);
        }
        helpers.updateField('IONumber', value);
      } else {
        const newEntry = {
          value,
          createdAt: new Date().toISOString(),
          __isStaged: true,
        };
        const updatedAdditional = [...currentAdditional, newEntry];

        helpers.updateField('additionalIONumbers', updatedAdditional);
        helpers.updateField('IONumber', value);
      }

      setIOSearchText('');
    } catch (error) {
      console.error('Error adding IO number:', error);
      message.error('Failed to add IO Number');
    }
  };

  // Prepare dropdown options
  const ioNumberSource = formValues.additionalIONumbers || currentCampaignOrder?.additionalIONumbers || [];

  const ioNumberOptions = [
    ...ioNumberSource.map((ioNumber, index) => ({
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{ioNumber.value}</span>
          <span
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Popconfirm
              placement="right"
              title="Are you sure you want to delete this IO Number?"
              onConfirm={() => handleDeleteIONumber(ioNumber.id, ioNumber.value)}
              okText="Yes"
              cancelText="No"
            >
              <DeleteOutlined />
            </Popconfirm>
          </span>
        </div>
      ),
      value: ioNumber.value,
      key: index + 1,
    })),
  ];

  return (
    <>
      <Card id="requester-details" title="DETAILS">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={20}>
            <Col span={10}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Advertiser</Text>
                <Text type="secondary">The one being promoted in this campaign</Text>
                <Form.Item
                  name="advertiserId"
                  rules={[
                    {
                      required: true,
                      message: 'Please select an advertiser',
                    },
                  ]}
                >
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    placeholder="Select Advertiser"
                    optionFilterProp="children"
                    loading={loadingChildrenOrgs}
                    onSearch={handleDebouncedAdvertiserSearch}
                    filterOption={(input, option) => {
                      return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                    options={advertisers}
                    disabled={!canEdit}
                    value={formValues.advertiserId}
                  />
                </Form.Item>
              </Space>
            </Col>
            <Col span={14}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Campaign Name</Text>
                <Text type="secondary">The name you will identify this campaign by</Text>
                <Form.Item
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter campaign name',
                    },
                    {
                      pattern: /^[a-zA-Z0-9\s\-_]*$/,
                      message:
                        'Only letters, numbers, spaces, hyphens, and underscores are allowed',
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter campaign name"
                    value={formValues.name}
                    onChange={handleNameChange}
                  />
                </Form.Item>
              </Space>
            </Col>
          </Row>

          <Row gutter={20}>
            <Col span={24}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Description</Text>
                <Text type="secondary">Optional: Helps to distinguish your campaign</Text>
                <Form.Item name="description">
                  <Input
                    placeholder="Enter optional description for campaign"
                    value={formValues.description}
                  />
                </Form.Item>
              </Space>
            </Col>
          </Row>

          <Row gutter={20}>
            <Col span={8}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>IO Number</Text>
                <Text type="secondary">IO number or account identifier</Text>
                <Form.Item
                  name="IONumber"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter IO Number',
                    },
                  ]}
                >
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    placeholder="Select or type new IO Number"
                    value={formValues.IONumber}
                    onChange={handleIONumberChange}
                    options={ioNumberOptions}
                    searchValue={ioSearchText}
                    onSearch={setIOSearchText}
                    onSelect={() => setIOSearchText('')}
                    filterOption={(input, option) =>
                      option.value.toLowerCase().includes(input.toLowerCase())
                    }
                    dropdownRender={(menu) => {
                      const trimmedSearch = ioSearchText.trim();
                      const exactMatchExists = ioNumberSource.some(
                        io => io.value.toLowerCase() === trimmedSearch.toLowerCase()
                      );
                      const showAddOption = trimmedSearch && !exactMatchExists;

                      return (
                        <>
                          {menu}
                          <Divider style={{ margin: '8px 0' }} />
                          {showAddOption ? (
                            <div
                              style={{ padding: '0 8px 4px', cursor: 'pointer', color: '#1890ff' }}
                              onClick={() => handleAddIOFromSearch(trimmedSearch)}
                            >
                              <PlusOutlined /> Add "{trimmedSearch}" as IO Number
                            </div>
                          ) : (
                            <div
                              style={{ padding: '0 8px 4px', cursor: 'pointer', color: '#1890ff' }}
                              onClick={() => setIsModalVisible(true)}
                            >
                              <PlusOutlined /> Add New IO Number
                            </div>
                          )}
                        </>
                      );
                    }}
                  />
                </Form.Item>
              </Space>
            </Col>
            <HasAccess roles={['isTrafficker']}>
              <Col span={8}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text>Renewal Status</Text>
                  <Text type="secondary">Renewal status of the campaign</Text>
                  <Form.Item name="renewalStatus">
                    <Select
                      style={{ width: '100%' }}
                      showSearch
                      placeholder="Expected to Renew"
                      value={formValues.renewalStatus}
                      options={[
                        {
                          label: 'Expected to Renew',
                          value: 'EXPECTED_TO_RENEW',
                          key: 1,
                        },
                        {
                          label: 'Not Renewing',
                          value: 'NOT_RENEWING',
                          key: 2,
                        },
                      ]}
                    />
                  </Form.Item>
                </Space>
              </Col>
            </HasAccess>
          </Row>
        </Space>
      </Card>
      <Form.Item name="productCode" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="additionalIONumbers" hidden>
        <Input />
      </Form.Item>

      <Modal
        title="Add New IO Number"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setIsModalVisible(false)}>
            Back
          </Button>,
          <Button key="submit" type="primary" onClick={handleAddNewIONumber}>
            Confirm
          </Button>,
        ]}
      >
        <Input
          placeholder="Enter IO Number"
          value={newIONumber}
          onChange={(e) => setNewIONumber(e.target.value)}
        />
      </Modal>
    </>
  );
};

export default CampaignRequestorDetails;